import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc, writeBatch } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import DexcoFormFields, { getEmptyDexcoFormValues } from "./dexco_form_fields";
import {
  buildDexcoPost,
  defaultDexcoMembers,
  DexcoCategory,
  DexcoMember,
  normalizeDexcoMember,
  sortDexcoMembers,
} from "../../dexco/dexco_data";
import { toTitleCaseName } from "../../../utils/title_case";

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  if (
    trimmedValue.startsWith("http://") ||
    trimmedValue.startsWith("https://") ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
}

async function resequenceDexcoMembers(category: DexcoCategory, targetMemberId?: string) {
  const snapshot = await getDocs(collection(firestore, "dexco_members"));
  const orderedDocs = snapshot.docs
    .filter((memberDoc) => memberDoc.data().category === category)
    .sort((docA, docB) => {
      const rankA = Number(docA.data().rank || 0);
      const rankB = Number(docB.data().rank || 0);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      if (targetMemberId && docA.id === targetMemberId) {
        return -1;
      }

      if (targetMemberId && docB.id === targetMemberId) {
        return 1;
      }

      return String(docA.data().name || "").localeCompare(String(docB.data().name || ""));
    });

  const batch = writeBatch(firestore);
  const timestamp = new Date().toISOString();

  orderedDocs.forEach((memberDoc, index) => {
    batch.update(memberDoc.ref, {
      rank: index + 1,
      updatedAt: timestamp,
    });
  });

  await batch.commit();
}

export default function ManageDexcoMembersTab() {
  const { showSnackbar } = useFeedback();
  const [members, setMembers] = useState<DexcoMember[]>([]);
  const [editingMember, setEditingMember] = useState<DexcoMember | null>(null);
  const [formValues, setFormValues] = useState(getEmptyDexcoFormValues());
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  async function seedDefaultMembers() {
    const timestamp = new Date().toISOString();

    await Promise.all(
      defaultDexcoMembers.map((member) =>
        setDoc(
          doc(
            firestore,
            "dexco_members",
            `${member.category}-${member.rank}-${member.name}`.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase(),
          ),
          {
            ...member,
            createdAt: timestamp,
            updatedAt: timestamp,
          },
        ),
      ),
    );
  }

  async function fetchMembers() {
    try {
      setIsLoading(true);
      const snapshot = await getDocs(collection(firestore, "dexco_members"));

      if (!snapshot.docs.length) {
        await seedDefaultMembers();
        const seededSnapshot = await getDocs(collection(firestore, "dexco_members"));
        setMembers(
          sortDexcoMembers(
            seededSnapshot.docs.map((memberDoc) =>
              normalizeDexcoMember({
                docId: memberDoc.id,
                ...(memberDoc.data() as Omit<DexcoMember, "docId">),
              }),
            ),
          ),
        );
        return;
      }

      setMembers(
        sortDexcoMembers(
          snapshot.docs.map((memberDoc) =>
            normalizeDexcoMember({
              docId: memberDoc.id,
              ...(memberDoc.data() as Omit<DexcoMember, "docId">),
            }),
          ),
        ),
      );
    } catch (error) {
      console.error("Failed to load dexco members", error);
      showSnackbar("Failed to load DEXCO members");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleEdit(member: DexcoMember) {
    setEditingMember(member);
    setFormValues({
      category: member.category,
      rank: String(member.rank),
      name: member.name,
      postType: member.postType || "Representative",
      postDetail: member.postDetail || "",
      deaneryName: member.deaneryName || "",
      place: member.place || "",
      img: member.img || "",
    });
    setSelectedImage(null);
  }

  async function handleUpdate() {
    if (!editingMember?.docId) {
      return;
    }

    const rank = Number(formValues.rank);

    if (!formValues.name.trim() || !Number.isInteger(rank) || rank <= 0) {
      showSnackbar("Please fill rank and name");
      return;
    }

    if (formValues.postType === "Representative" && !formValues.postDetail.trim()) {
      showSnackbar("Please enter the representative designation");
      return;
    }

    if (formValues.postType === "Deanery President" && !formValues.deaneryName.trim()) {
      showSnackbar("Please select the deanery");
      return;
    }

    try {
      setIsSaving(true);
      let finalImage = formValues.img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `dexco_members/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const timestamp = new Date().toISOString();
      const previousCategory = editingMember.category;

      await updateDoc(doc(firestore, "dexco_members", editingMember.docId), {
        category: formValues.category,
        rank,
        name: toTitleCaseName(formValues.name),
        postType: formValues.postType,
        postDetail: formValues.postDetail.trim(),
        deaneryName: formValues.deaneryName.trim(),
        post: buildDexcoPost(
          formValues.postType,
          formValues.postDetail,
          formValues.deaneryName,
        ),
        place: formValues.place.trim(),
        img: normalizeUrl(finalImage),
        updatedAt: timestamp,
      });

      await resequenceDexcoMembers(formValues.category, editingMember.docId);

      if (previousCategory !== formValues.category) {
        await resequenceDexcoMembers(previousCategory);
      }

      showSnackbar("DEXCO member updated successfully");
      setEditingMember(null);
      setFormValues(getEmptyDexcoFormValues());
      setSelectedImage(null);
      fetchMembers();
    } catch (error) {
      console.error("Failed to update dexco member", error);
      showSnackbar("Failed to update DEXCO member");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(member: DexcoMember) {
    if (!member.docId) {
      return;
    }

    if (!window.confirm(`Delete "${member.name}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(firestore, "dexco_members", member.docId));
      await resequenceDexcoMembers(member.category);
      showSnackbar("DEXCO member deleted successfully");
      fetchMembers();
    } catch (error) {
      console.error("Failed to delete dexco member", error);
      showSnackbar("Failed to delete DEXCO member");
    }
  }

  if (isLoading) {
    return (
      <Box py={6} textAlign="center">
        <Typography>Loading DEXCO members...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        {members.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.docId || `${member.category}-${member.rank}-${member.name}`}>
            <Card sx={{ height: "100%" }}>
              {member.img ? (
                <Box
                  component="img"
                  src={member.img}
                  alt={member.name}
                  sx={{
                    width: "100%",
                    height: 240,
                    objectFit: "cover",
                    objectPosition: "center top",
                  }}
                />
              ) : null}
              <CardContent>
                <Box display="flex" gap={1} alignItems="center" mb={1} flexWrap="wrap">
                  <Chip label={`${member.category} DEXCO`} color="primary" size="small" />
                  <Chip label={`Rank ${member.rank}`} variant="outlined" size="small" />
                </Box>
                <Typography variant="h6" fontWeight={700}>
                  {member.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.post}
                </Typography>
                {member.place ? (
                  <Typography variant="body2" color="text.secondary">
                    {member.place}
                  </Typography>
                ) : null}
                <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                  <Button variant="outlined" startIcon={<Edit />} onClick={() => handleEdit(member)}>
                    Edit
                  </Button>
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => handleDelete(member)}>
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editingMember)} onClose={() => setEditingMember(null)} fullWidth maxWidth="md">
        <DialogTitle>Edit DEXCO Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} mt={0.5}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: "none", border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <DexcoFormFields formValues={formValues} setFormValues={setFormValues} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: "none", border: "1px solid rgba(15, 23, 42, 0.08)" }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" flexDirection="column" gap={2.5}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={formValues.img}
                      onChange={(event) => {
                        setFormValues((prev) => ({ ...prev, img: event.target.value }));
                        if (event.target.value) {
                          setSelectedImage(null);
                        }
                      }}
                    />
                    <FormControl fullWidth>
                      <FormLabel>Upload New Member Image</FormLabel>
                      <input
                        type="file"
                        accept="image/*"
                        style={{
                          border: "1px solid rgb(181, 176, 176)",
                          width: "96%",
                          paddingLeft: 8,
                          paddingRight: 8,
                          paddingTop: 16,
                          paddingBottom: 16,
                          borderRadius: 5,
                          marginTop: 8,
                        }}
                        onChange={(event) => {
                          if (event.target.files?.[0]) {
                            setSelectedImage(event.target.files[0]);
                            setFormValues((prev) => ({ ...prev, img: "" }));
                          }
                        }}
                      />
                      {selectedImage ? (
                        <Box mt={2} position="relative" display="inline-block">
                          <img
                            src={URL.createObjectURL(selectedImage)}
                            alt="Updated DEXCO preview"
                            style={{
                              maxWidth: "220px",
                              maxHeight: "220px",
                              borderRadius: 8,
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setSelectedImage(null)}
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              color: "#ffffff",
                              backgroundColor: "rgba(0,0,0,0.5)",
                            }}
                          >
                            <Close />
                          </IconButton>
                        </Box>
                      ) : formValues.img ? (
                        <Box mt={2}>
                          <Box
                            component="img"
                            src={formValues.img}
                            alt="Current member"
                            sx={{
                              maxWidth: 220,
                              maxHeight: 220,
                              borderRadius: 2,
                            }}
                          />
                        </Box>
                      ) : null}
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMember(null)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleUpdate} variant="contained" disabled={isSaving}>
            {isSaving ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
